import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Briefcase, Plus, X } from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Input, Select } from '../../components/ui/Form.jsx';

// =============================================================================
// SalesExecCard — customer page: which sales exec earns commission on them
// =============================================================================
// Owner-only (the parent only renders it for owners). A client normally has
// one current exec. Changing exec = end the current link and add a new one,
// so past bookings stay with the exec who had the client at the time.
// =============================================================================

export function SalesExecCard({ customerId }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [adding, setAdding] = useState(false);
    const [ending, setEnding] = useState(null);

    const linksQuery = useQuery({
        queryKey: ['sales-exec-links', customerId],
        queryFn: () => api.get(`/customers/${customerId}/sales-exec-links`),
    });

    const removeMutation = useMutation({
        mutationFn: (linkId) => api.delete(`/sales-exec-links/${linkId}`),
        onSuccess: () => {
            toast.success('Link removed');
            queryClient.invalidateQueries({ queryKey: ['sales-exec-links', customerId] });
        },
        onError: (err) => toast.error(err.message || 'Could not remove'),
    });

    const today = new Date().toISOString().slice(0, 10);
    const links = linksQuery.data?.links || [];
    const current = links.filter((l) => !l.end_date || String(l.end_date).slice(0, 10) >= today);
    const past = links.filter((l) => l.end_date && String(l.end_date).slice(0, 10) < today);

    return (
        <div className="card">
            <div className="card-header flex items-center justify-between gap-2">
                <div>
                    <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
                        <Briefcase className="h-4 w-4 text-ink-subtle" />
                        Sales exec
                    </h2>
                    <p className="text-xs text-ink-subtle mt-0.5">Earns commission on this client’s invoiced bookings.</p>
                </div>
                <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
                    <Plus className="h-3.5 w-3.5" />
                    {current.length > 0 ? 'Add' : 'Assign'}
                </Button>
            </div>
            <div className="card-body space-y-3">
                {linksQuery.isLoading && <p className="text-sm text-ink-subtle">Loading…</p>}
                {linksQuery.isSuccess && links.length === 0 && (
                    <p className="text-sm text-ink-subtle">No sales exec assigned.</p>
                )}
                {current.map((l) => (
                    <LinkRow
                        key={l.id}
                        link={l}
                        onEnd={() => setEnding(l)}
                        onRemove={() => {
                            if (window.confirm(`Remove ${l.sales_exec_name} from this client completely? Use "End" instead if they had the client until now.`)) {
                                removeMutation.mutate(l.id);
                            }
                        }}
                    />
                ))}
                {past.length > 0 && (
                    <div className="pt-2 border-t border-paper-border-soft">
                        <div className="text-2xs uppercase tracking-wider text-ink-subtle mb-1.5">Previous</div>
                        {past.map((l) => (
                            <div key={l.id} className="text-xs text-ink-subtle">
                                {l.sales_exec_name} · {pctLabel(l)} · {fmtDate(l.start_date)} – {fmtDate(l.end_date)}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {adding && (
                <AddLinkModal
                    customerId={customerId}
                    hasCurrent={current.length > 0}
                    onClose={() => setAdding(false)}
                />
            )}
            {ending && (
                <EndLinkModal
                    link={ending}
                    customerId={customerId}
                    onClose={() => setEnding(null)}
                />
            )}
        </div>
    );
}

function LinkRow({ link, onEnd, onRemove }) {
    return (
        <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
                <div className="text-sm font-medium text-ink">{link.sales_exec_name}</div>
                <div className="text-xs text-ink-subtle">
                    {pctLabel(link)} · from {fmtDate(link.start_date)}
                    {link.end_date ? ` until ${fmtDate(link.end_date)}` : ''}
                </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
                <button type="button" onClick={onEnd} className="text-xs text-ink-muted hover:text-ink hover:underline">
                    {link.end_date ? 'Change end' : 'End'}
                </button>
                <button type="button" onClick={onRemove} className="p-1 text-ink-subtle hover:text-status-danger" aria-label="Remove">
                    <X className="h-3.5 w-3.5" />
                </button>
            </div>
        </div>
    );
}

function AddLinkModal({ customerId, hasCurrent, onClose }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const execsQuery = useQuery({
        queryKey: ['sales-execs'],
        queryFn: () => api.get('/sales-execs'),
    });
    const execs = (execsQuery.data?.sales_execs || []).filter((e) => e.status === 'active');

    const [execId, setExecId] = useState('');
    const [pctValue, setPctValue] = useState('');
    const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
    const [endDate, setEndDate] = useState('');

    const selectedId = execId || execs[0]?.id || '';
    const selected = execs.find((e) => e.id === selectedId);
    const pctNum = pctValue.trim() === '' ? null : Number(pctValue);
    const pctValid = pctNum === null || (Number.isFinite(pctNum) && pctNum >= 0 && pctNum <= 100);

    const mutation = useMutation({
        mutationFn: () => api.post(`/customers/${customerId}/sales-exec-links`, {
            sales_exec_id: selectedId,
            commission_pct: pctNum,
            start_date: startDate,
            end_date: endDate || null,
        }),
        onSuccess: () => {
            toast.success('Sales exec assigned');
            queryClient.invalidateQueries({ queryKey: ['sales-exec-links', customerId] });
            onClose();
        },
        onError: (err) => toast.error(err.message || 'Could not assign'),
    });

    return (
        <Modal open onClose={onClose} title="Assign sales exec" size="md">
            <div className="space-y-3">
                {execsQuery.isSuccess && execs.length === 0 ? (
                    <p className="text-sm text-ink-subtle">
                        No sales execs yet — add them in Reports → Sales exec statements → Manage sales execs.
                    </p>
                ) : (
                    <>
                        {hasCurrent && (
                            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                                This client already has a sales exec. If you’re replacing them, click <strong>End</strong> on
                                the current one first — otherwise both earn commission.
                            </p>
                        )}
                        <Select label="Sales exec" value={selectedId} onChange={(e) => setExecId(e.target.value)}>
                            {execs.map((e) => (
                                <option key={e.id} value={e.id}>{e.name} ({Number(e.default_commission_pct)}%)</option>
                            ))}
                        </Select>
                        <Input
                            label="Commission % for this client (optional)"
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={pctValue}
                            onChange={(e) => setPctValue(e.target.value)}
                            placeholder={selected ? `${Number(selected.default_commission_pct)} (exec default)` : ''}
                            hint="Leave blank to use the exec’s default."
                        />
                        <div className="grid grid-cols-2 gap-3">
                            <Input label="From" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                            <Input label="Until (optional)" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                        </div>
                        <p className="text-xs text-ink-subtle">Bookings with a pickup date in this window count towards the exec’s commission.</p>
                    </>
                )}
                <div className="flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                    <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
                    <Button
                        variant="primary"
                        onClick={() => mutation.mutate()}
                        loading={mutation.isPending}
                        disabled={!selectedId || !pctValid || !startDate || (endDate && endDate < startDate)}
                    >
                        Assign
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

function EndLinkModal({ link, customerId, onClose }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [endDate, setEndDate] = useState(
        link.end_date ? String(link.end_date).slice(0, 10) : new Date().toISOString().slice(0, 10)
    );

    const mutation = useMutation({
        mutationFn: () => api.patch(`/sales-exec-links/${link.id}`, { end_date: endDate || null }),
        onSuccess: () => {
            toast.success('Updated');
            queryClient.invalidateQueries({ queryKey: ['sales-exec-links', customerId] });
            onClose();
        },
        onError: (err) => toast.error(err.message || 'Could not update'),
    });

    return (
        <Modal
            open
            onClose={onClose}
            title={`End ${link.sales_exec_name}`}
            description="Bookings up to and including this date still count for them."
            size="md"
        >
            <Input label="Last day" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
                <Button variant="primary" onClick={() => mutation.mutate()} loading={mutation.isPending}>Save</Button>
            </div>
        </Modal>
    );
}

function pctLabel(l) {
    return l.commission_pct != null
        ? `${Number(l.commission_pct)}%`
        : `${Number(l.default_commission_pct)}% (default)`;
}

function fmtDate(d) {
    if (!d) return '';
    const dt = new Date(`${String(d).slice(0, 10)}T00:00:00`);
    return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
